<template>
  <div class="document-viewer" ref="documentViewer">
    <div class="document-viewer__toolbar">
      <q-btn
        icon="add"
        class="q-mr-sm"
        size="sm"
        flat
        dense
        @click="handleZoomIn"
      />
      <q-btn
        icon="remove"
        class="q-mr-sm"
        size="sm"
        flat
        dense
        @click="handleZoomOut"
      />
      <q-select
        v-model="zoomFactor"
        :options="zoomOptions"
        :display-value="`${Math.round(100 * this.zoomFactor).toString()} %`"
        color="white"
        class="q-mr-md"
        dark
        hide-underline
      />
      <q-btn
        v-if="showDownloadButton"
        icon="cloud_download"
        size="sm"
        flat
        dense
        @click="handleDownload"
      />
    </div>
    <div class="document-viewer__content" :style="{ height: contentHeight }">
      <template v-if="!fileIsLoaded">
        <q-spinner color="white" />
      </template>
      <template v-else-if="fileIsImage">
        <div v-dragscroll>
          <img :src="base64" :style="{ width: zoom }" @dragstart.prevent>
        </div>
      </template>
      <template v-else-if="fileIsPdf">
        <div v-dragscroll>
          <pdf
            v-for="page in pdfNumPages"
            :key="page"
            :src="pdfSrc"
            :page="page"
            :style="{ width: zoom }"
            class="document-viewer__pdf-page"
          />
        </div>
      </template>
      <template v-else>
        Kann Datei nicht anzeigen
      </template>
    </div>
  </div>
</template>

<script>
  import { DocumentsClient } from 'src/api/Documents'
  import FileSaver from 'file-saver'
  import { dragscroll } from 'vue-dragscroll'
  import pdf from 'vue-pdf'

  export default {
    components: {
      pdf
    },
    directives: {
      dragscroll
    },
    data () {
      return {
        file: null,
        fileIsLoaded: false,
        base64: null,
        pdfSrc: null,
        pdfNumPages: 1,
        zoomFactor: 1,
        contentHeight: '70vh'
      }
    },
    props: {
      document: {
        type: Object,
        required: true
      },
      showDownloadButton: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    computed: {
      fileIsPdf () {
        return this.document.mime_type.includes('application/pdf')
      },
      fileIsImage () {
        return ['image/jpg', 'image/png'].includes(this.document.mime_type)
      },
      zoom () {
        return `${Math.round(100 * this.zoomFactor).toString()}%`
      },
      zoomOptions () {
        return [
          { label: '50 %', value: 0.5 },
          { label: '75 %', value: 0.75 },
          { label: '100 %', value: 1 },
          { label: '125 %', value: 1.25 },
          { label: '150 %', value: 1.5 },
          { label: '200 %', value: 2 },
          { label: '300 %', value: 3 },
          { label: '400 %', value: 4 }
        ]
      }
    },
    methods: {
      fetchFile () {
        new DocumentsClient()
          .file(this.document.id)
          .then((response) => {
            const reader = new FileReader()
            reader.addEventListener('load', () => {
              this.base64 = reader.result
              this.file = response.data

              if (this.fileIsPdf) {
                this.pdfSrc = pdf.createLoadingTask(this.base64)
                this.pdfSrc.then((pdf) => {
                  this.pdfNumPages = pdf.pdfInfo.numPages
                  this.fileIsLoaded = true
                })
              } else {
                this.fileIsLoaded = true
              }
            })
            reader.readAsDataURL(new Blob([response.data], { type: this.document.mime_type }))
          })
      },
      reset () {
        this.fileIsLoaded = false
        this.file = null
        this.base64 = null
        this.pdfSrc = null
        this.pdfNumPages = 1
        this.zoomFactor = 1
      },
      handleZoomIn () {
        this.zoomFactor += 0.1
      },
      handleZoomOut () {
        if (this.zoomFactor > 0.6) {
          this.zoomFactor -= 0.1
        }
      },
      handleDownload () {
        new DocumentsClient()
          .file(this.document.id)
          .then(response => {
            const blob = new Blob([response.data], { type: this.document.mime_type })
            FileSaver.saveAs(blob, this.document.original_name ? this.document.original_name : this.document.uuid + this.document.mime_type.split('/').slice(-1)[0])
          })
      }
    },
    created () {
      this.fetchFile()
    },
    watch: {
      document () {
        this.reset()
        this.fetchFile()
      }
    },
    mounted () {
      const layout = document.getElementsByClassName('layout-padding')[0]
      const toolbarHeight = 40

      // Hack to force the document viewer into the layout height
      const calculateDocumentViewerContentHeight = () => {
        const style = window.getComputedStyle(layout)

        const height = style.getPropertyValue('height')
        const paddingTop = style.getPropertyValue('padding-top')
        const paddingBottom = style.getPropertyValue('padding-bottom')

        this.contentHeight = `min(80vh, calc(${height} - ${paddingTop} - ${paddingBottom} - ${toolbarHeight}px))`
      }

      window.addEventListener('resize', calculateDocumentViewerContentHeight)
      calculateDocumentViewerContentHeight()
    }
  }
</script>

<style lang="stylus">
  @import '~variables'

  .document-viewer
    height 100%
    height 100%
    display flex
    flex-direction column
    background-color $grey-9
    color white

    .document-viewer__toolbar
      display flex
      justify-content center
      padding .5rem 1rem
      background-color $grey-10
      color white

    .document-viewer__content
      width 100%
      padding .5rem
      text-align center
      > div
        width 100%
        height 100%
        overflow hidden
        overflow-y scroll
        scrollbar-visibility hidden
        scrollbar-width none
        cursor grab
        &:active
          cursor move

        &::-webkit-scrollbar
            display none

      .document-viewer__pdf-page
        margin 0 auto
        margin-bottom 0.5rem
</style>
